import { optionService } from "../option";
import {
    RegionElement,
    StepService,
    ColorOption,
    CommandWithFollowup,
    ShapeStepData,
    Step,
    VariantResource,
    ILayout,
    LayoutsState,
    Region,
    ColorDefinition,
    IllustrationElement,
    LayoutElementType,
} from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { LayoutNotFoundError, OptionNotFoundError } from "../../util/exception";
import { CanvasCommand, CreateElementCommand, GroupCommand, IllustrationColorCommand } from "../../command";
import { generate } from "../../util/guid";

class ShapeStepService implements StepService<ShapeStepData> {
    readonly shapeFillId = "spiff-fill-shape";

    // Get the step's options.
    async availableColours(stepData: Step<ShapeStepData>) {
        const option = stepData.option;
        if (option) {
            return (
                option.variants?.map((variant: VariantResource) => {
                    return {
                        fill: variant.color,
                        stroke: variant.color,
                        variant,
                    };
                }) || []
            );
        }
        return [];
    }

    setCustomColor(color: string, stepData: Step<ShapeStepData>, manager: WorkflowManager) {
        const layouts = manager.getLayouts();
        const elements = manager.getRegionElements(stepData.stepName) || [];
        const commands = elements
            .map((stepElement: RegionElement) => {
                const region = stepElement.region;
                const layout = layouts.find((layout: ILayout) => layout.panelId === region?.panelId);
                if (!layout) throw new LayoutNotFoundError(region!);
                return new IllustrationColorCommand(stepElement.id, this.shapeFillId, color);
            })
            .filter((c) => !!c);
        manager.getCommandDispatcher()(new GroupCommand(commands));
        manager.updateStorage(stepData.stepName, { colour: color });
    }

    async init(
        stepData: Step<ShapeStepData>,
        workflowManager: WorkflowManager,
        reducerState?: LayoutsState,
    ): Promise<CommandWithFollowup | null> {
        const option = stepData.option;
        if (!option) {
            throw new OptionNotFoundError(stepData);
        }
        if (reducerState) {
            await this.reload(stepData, workflowManager, reducerState);
        } else {
            const variant = optionService.getDefaultVariant(option);
            if (!variant) {
                return null;
            }
            return this.selectVariantCommand(
                stepData,
                {
                    fill: variant.color,
                    stroke: variant.color,
                    variant,
                },
                [],
                workflowManager,
            );
        }
        return null;
    }

    async selectVariant(
        stepData: Step<ShapeStepData>,
        colourOption: ColorOption,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ) {
        const result = await this.selectVariantCommand(stepData, colourOption, elements, workflowManager);
        if (result) {
            result.command && workflowManager.getCommandDispatcher()(result.command);
            result.followup && (await result.followup());
        }
    }

    private async reload(stepData: Step<ShapeStepData>, workflowManager: WorkflowManager, reducerState: LayoutsState) {
        const serializedStep = workflowManager.getSerializedStep(
            stepData.stepName,
            reducerState.serializableWorkflow.steps,
        );
        const layouts = Object.values(reducerState.layouts);
        const elements = layouts.map((layout) => layout.elements).flat();
        const elementsForStep = elements.filter((el) => el.stepName === stepData.stepName);
        const option = stepData.option;
        if (serializedStep?.selectedVariants) {
            const variantId = serializedStep.selectedVariants[0].id;
            if (option && variantId) {
                const variant = option.variants?.find((variant) => variant.id === variantId);
                if (variant) {
                    const regionElements = elementsForStep.map((el) => ({
                        id: el.id,
                        region: el.stepRegion,
                        regionIndex: el.stepRegionIndex,
                    }));
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        [variant],
                        regionElements,
                        async () => {
                            workflowManager.updateStorage(stepData.stepName, {
                                colour: serializedStep?.storage?.colour || "",
                            });
                            workflowManager.setMandatoryFulfilled(stepData.stepName, true);
                        },
                    );
                }
            }
        }
    }

    private async selectVariantCommand(
        stepData: Step<ShapeStepData>,
        colourOption: ColorOption,
        elements: RegionElement[],
        workflowManager: WorkflowManager,
    ): Promise<CommandWithFollowup | null> {
        const layouts = workflowManager.getLayouts();
        const existingColor = workflowManager.getStepStorage(stepData.stepName)?.colour;
        const getColor = () => {
            if (colourOption.variant?.color === "#custom") return existingColor || "#FFFFFF";
            if (colourOption.variant?.color) return colourOption.variant.color;
            throw new Error("Failed to resolve color for shape step.");
        };
        if (elements.length > 0) {
            const updateCommand = (stepElement: RegionElement) => {
                const region = stepElement.region;
                const layout = layouts.find((layout: ILayout) => layout.panelId === region?.panelId);
                if (!layout) {
                    throw new LayoutNotFoundError(region!);
                }
                const newColor = getColor();
                return new IllustrationColorCommand(stepElement.id, this.shapeFillId, newColor);
            };
            const commands = elements.map(updateCommand).filter((c) => !!c);
            return {
                command: new GroupCommand(commands as CanvasCommand[]),
                followup: async () => {
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        colourOption.variant ? [colourOption.variant] : [],
                        elements,
                    );
                    workflowManager.updateStorage(stepData.stepName, { colour: getColor() });
                },
            };
        } else {
            // No existing elements, create new ones.
            const createElementForRegion = (region: Region, regionIndex: number) => {
                const layout = layouts.find((layout: ILayout) => layout.panelId === region.panelId);
                if (!layout) {
                    throw new LayoutNotFoundError(region);
                }
                const newColor = getColor();
                const svg = `
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        xmlSpace="preserve"
                        preserveAspectRatio="none"
                        version="1.1"
                        width="1"
                        height="1"
                        viewBox="0 0 1 1"
                    >
                        <rect
                            x="0"
                            y="0"
                            width="1"
                            height="1"
                            class="${this.shapeFillId}"
                            fill="${newColor}"
                        />
                    </svg>
                `;
                const colors: { [key: string]: ColorDefinition } = {};
                colors[this.shapeFillId] = { browserValue: newColor };

                const elementKey = generate();
                return {
                    id: elementKey,
                    region,
                    command: new CreateElementCommand<IllustrationElement>(
                        {
                            stepRegion: region,
                            stepRegionIndex: regionIndex,
                            stepName: stepData.stepName,
                            colors,
                            id: elementKey,
                            svg,
                            type: LayoutElementType.Illustration,
                            y: region.top,
                            x: region.left,
                            rotation: region.rotation,
                            width: region.width,
                            height: region.height,
                            layer: region.layer,
                            layerIndex: region.layerIndex,
                            immutable: region.immutable,
                            excludeFromExport: stepData.data.excludeFromPrint,
                        },
                        layout,
                    ),
                };
            };
            const results = stepData.data.regions.map(createElementForRegion);
            const commands = results.filter((r) => !!r).map((r) => r?.command);
            const elements = results
                .filter((r) => !!r)
                .map((r, index) => {
                    return { id: r!.id, region: r!.region, regionIndex: index };
                });
            return {
                command: new GroupCommand(commands as CanvasCommand[]),
                followup: async () => {
                    await workflowManager.setSelectionsAndElements(
                        stepData.stepName,
                        colourOption.variant ? [colourOption.variant] : [],
                        elements,
                        async () => {
                            workflowManager.updateStorage(stepData.stepName, { colour: getColor() });
                        },
                    );
                },
            };
        }
    }
}

export const shapeStepService = new ShapeStepService();
